// measurementHandler
// -> Manages measurement and widget if necessary

// Import Required Classes
import EventEmitter from "events";

// Import ESRI Classes
import Query from "@arcgis/core/rest/support/Query";
import * as query from "@arcgis/core/rest/query.js";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";

// Import the Functions file
import {
  numberWithCommas,
  renderDecimalMinuteSeconds,
} from "../../Shared/Functions";

// React Widgets
import DrawHandler from "./DrawHandler";

// Extend the EventEmitter to allow other logic to listen for sidebar events
export default class MeasurementHandler extends EventEmitter {
  constructor(args = {}) {
    super();

    // Parse the arguments
    this.view = args.view;
    this.render_widget = false;
    this.selected_tool = null;
    this.click_handler = null;
    this.mouse_handler = null;
    this.parcel_handler = null;
    this.moved = false;
    this.$map = document.querySelector(".esri-view-surface");

    // Store the values
    this.distance = 0;
    this.lengthUnit = "feet";
    this.areaUnit = "square-feet";
    this.locUnit = "Lat/Lon";

    this.setActive = args.setActive;
    this.setTooltipMessage = args.setTooltipMessage;
    this.parcels_service_url = args.parcels_service_url;

    this.setGeometry = args.setGeometry;
    this.setLengthUnit = args.setLengthUnit;
    this.setAreaUnit = args.setAreaUnit;
    this.setLocUnit = args.setLocUnit;
    this.setLength = args.setLength;
    this.setArea = args.setArea;
    this.setDistance = args.setDistance;
    this.setBearing = args.setBearing;
    this.setUnit = args.setUnit;

    // Initialize when the view is loaded
    // => Apparently ESRI can't just trip this if the view is loaded... so I have to check the property manually as well
    this.initialize();
  }

  initialize() {
    // Add listeners to clear the labels because ESRI is too slow
    // => Only clear it if the user does a full click, not a click and drag
    this.$map.addEventListener("mousedown", () => {
      this.moved = false;
    });
    this.$map.addEventListener("mousemove", () => {
      this.moved = true;
    });
    this.$map.addEventListener("mouseup", () => {
      if (!this.moved) {
        this.cancelMouseLabel();
      }
    });

    // Render the widget if requested
    // If not then listen for map click
    // if (this.render_widget) {
    // Create the Graphics Layer
    this.layer = new GraphicsLayer({
      id: "MeasurementLayer",
      print: true,
    });

    // Create the Graphics Layer for Labels
    this.label_layer = new GraphicsLayer({
      id: "LabelLayer",
      print: true,
    });

    // Create the Graphics Layer for the Mouse Label
    this.mouse_label_layer = new GraphicsLayer({
      id: "MouseLabelLayer",
      print: true,
    });

    // Add the layer to the map
    this.view.map.layers.addMany([
      this.layer,
      this.mouse_label_layer,
      this.label_layer,
    ]);

    // Widgets for the drawing tool
    this.draw_handler = new DrawHandler({
      view: this.view,
      layer: this.layer,
    });

    // Listen for the draw handler to be completed
    this.draw_handler.on("completed", (geometry) => {
      // console.error("completed", geometry, this.selected_tool);

      //   this.cancelTooltip();
      this.setTooltipMessage(null);
      this.setActive(false);

      this.$map.removeEventListener(
        "mouseup",
        this.cancelMouseLabel.bind(this)
      );

      if (this.selected_tool !== "parcel") {
        if (geometry.type === "polygon") {
          let length = geometry.rings[0].length - 1;

          var endPoint = {
            type: "point", // autocasts as new Point()
            x: geometry.rings[0][0][0],
            y: geometry.rings[0][0][1],
            spatialReference: this.view.spatialReference,
          };

          var startPoint = {
            type: "point", // autocasts as new Point()
            x: geometry.rings[0][length][0],
            y: geometry.rings[0][length][1],
            spatialReference: this.view.spatialReference,
          };

          this.calculateSimpleDistance(startPoint, endPoint, (result) => {
            this.renderMidPoint(result, (graphic) => {
              this.label_layer.graphics.add(graphic);
            });
          });
        }

        // this.dom_root.render(
        //   <MeasurementWidget
        //     geometry={geometry}
        //     lengthUnit={this.lengthUnit}
        //     areaUnit={this.areaUnit}
        //     locUnit={this.locUnit}
        //   />
        // );

        this.setGeometry(geometry);
        // console.log("SET GEOMETRY", {geometry})

        if (geometry.type === "polygon") {
          this.setAreaUnit(this.areaUnit);
          this.setLengthUnit(this.lengthUnit);
        } else if (geometry.type === "polyline") {
          this.setLengthUnit(this.lengthUnit);
        } else {
          this.setLocUnit(this.locUnit);
        }
      } else {
        this.calculateParcelDimension(geometry);
      }

      this.clearClickHandler();
      this.cancelMouseLabel();
    });

    // Render the Identify React Widget
    // this.output_node = document.createElement("div");
    // this.view.ui.add(this.output_node, "bottom-left");
    //   this.dom_root = ReactDOMClient.createRoot(this.output_node);
    // }
  }

  cancelMouseLabel() {
    this.mouse_handler && this.mouse_handler.remove();
    this.mouse_handler = null;
  }

  // Starts up the draw tool and listens for completion
  // => TODO make this it's own ES6 Class
  start = (tool) => {
    this.setActive(true);

    // reset the units
    this.setLengthUnit(null)
    this.setAreaUnit(null)
    this.setLocUnit(null)
    this.setUnit(null)

    // Reset the distance and unit
    this.distance = 0;

    // Set the selected tool
    this.selected_tool = tool;

    // Remove the identify handler if set
    if (["polyline", "rectangle", "polygon"].includes(tool)) {
      this.clearGraphics();
      this.draw_handler.start(tool);
      this.view.focus();

      // launch the tooltip
      this.setTooltipMessage(
        "Click to start drawing, double clicking will complete the feature"
      );

      this.points = [];
      this.midpoints = [];

      this.outSpatialReference = new SpatialReference({
        wkid: 4326,
      });

      // Remove all the layers
      this.label_layer.graphics.removeAll();

      this.clearClickHandler();
      this.click_handler = this.view.on("click", (event) => {
        this.points.push(event);

        // Caclulate the distance of the current point and the mouse pointer
        this.cancelMouseLabel();
        this.mouse_handler = this.view.on("pointer-move", (mouse_event) => {
          var point = this.view.toMap({
            x: mouse_event.x,
            y: mouse_event.y,
          });

          // Calculate the distance
          this.calculateSimpleDistance(event.mapPoint, point, (result) => {
            this.renderMidPoint(result, (graphic) => {
              //   this.dom_root.render(
              //     <MeasurementOutputWidget
              //       distance={this.distance.toFixed(4).toLocaleString()}
              //       bearing={result.bearing}
              //       unit={this.lengthUnit}
              //     />
              //   );

              this.setDistance(this.distance.toFixed(4).toLocaleString());
              this.setBearing(result.bearing);
              this.setUnit(this.lengthUnit);

              this.mouse_label_layer.graphics.removeAll();
              this.mouse_label_layer.graphics.add(graphic);
            });
          });
        });

        if (this.points.length >= 2) {
          var index = this.points.length - 2;

          this.calculateSimpleDistance(
            this.points[index].mapPoint,
            this.points[index + 1].mapPoint,
            (result) => {
              this.renderMidPoint(result, (graphic) => {
                // this.dom_root.render(
                //   <MeasurementOutputWidget
                //     distance={this.distance.toFixed(4).toLocaleString()}
                //     bearing={result.bearing}
                //     unit={this.lengthUnit}
                //   />
                // );

                // this.setDistance(this.distance.toFixed(4).toLocaleString());
                // this.setBearing(result.bearing);
                // this.setUnit(this.lengthUnit);

                this.label_layer.graphics.add(graphic);
              });
            }
          );
        }
      });
    } else if (tool === "parcel") {
      // launch the tooltip
      this.setTooltipMessage("Select a parcel to retrieve the area and length");

      this.clearGraphics();
      this.parcel_handler = this.view.on("click", (event) => {
        this.calculateParcelDimension(event.mapPoint);
      });
      this.view.focus();
    } else {
      // launch the tooltip
      this.setTooltipMessage("Click to retrieve the lat/lon");

      this.clearGraphics();
      this.draw_handler.start(tool);
      this.view.focus();
    }
  };

  renderMidPoint(measurement, callback) {
    var midpoint = {
      type: "point", // autocasts as new Point()
      x: measurement.xAvg,
      y: measurement.yAvg,
      spatialReference: this.view.spatialReference,
    };

    // add the graphic to the view
    var graphic = new Graphic({
      geometry: midpoint,
      symbol: {
        type: "text",
        text: numberWithCommas(measurement.distance.toFixed(2)),
        color: "white",
        haloColor: "black",
        haloSize: "1px",
        font: { size: 12, weight: "bold" },
        angle: measurement.angle,
        outline: {
          color: [0, 0, 0, 0.1],
          width: 0.5,
        },
      },
    });

    this.distance = measurement.distance;

    callback(graphic);
  }

  calculateSimpleDistance(from, to, callback) {
    // console.log("calculateSimpleDistance", from, to);
    // Get the X value
    var xs = (from.x - to.x) * (from.x - to.x);
    var ys = (from.y - to.y) * (from.y - to.y);
    var ld = Math.sqrt(xs + ys);
    var yAvg = (from.y + to.y) / 2;
    var xAvg = (from.x + to.x) / 2;

    this.calculateBearing(from, to, (result) => {
      callback({
        distance: ld,
        xAvg: xAvg,
        yAvg: yAvg,
        bearing: result.bearing,
        angle: result.angle,
      });
    });
  }

  // Calculate the Bearing and return the angle and label
  calculateBearing(from, to, callback) {
    var bearLbl = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
    var angle = bearLbl;
    if (angle < 0) {
      angle = Math.abs(angle);
      if (angle <= 180 && angle > 90) {
        angle = 180 + angle;
      }
    } else {
      angle = 360 - angle;
      if (angle <= 270 && angle > 180) {
        angle = 180 + angle;
      }
    }

    var ns = "N";
    var ew;
    if (bearLbl > 0 && bearLbl <= 90) {
      bearLbl = Math.abs(90 - bearLbl);
      ew = "E";
    } else if (bearLbl > 90 && bearLbl <= 180) {
      ew = "W";
      bearLbl = Math.abs(90 - (180 - bearLbl));
    } else if (bearLbl < 0) {
      ns = "S";
      bearLbl = Math.abs(bearLbl);
      if (bearLbl > 0 && bearLbl <= 90) {
        ew = "E";
        bearLbl = Math.abs(90 - bearLbl);
      } else if (bearLbl > 90 && bearLbl <= 180) {
        ew = "W";
        bearLbl = Math.abs(90 - (180 - bearLbl));
      }
    }

    callback({
      bearing: ns + " " + renderDecimalMinuteSeconds(bearLbl) + " " + ew,
      angle: angle,
    });
  }

  // Cancel the Tool
  cancel = () => {
    this.setActive(false);
    // this.dom_root.render(null);

    this.clearGraphics();
    if (this.parcel_handler && this.parcel_handler !== null) {
      this.parcel_handler.remove();
      this.parcel_handler = null;
    }
    // clear the tooltips
    // this.cancelTooltip();
    this.setTooltipMessage(null);
  };

  // Clear the Graphics
  clearGraphics = () => {
    this.layer.graphics.removeAll();
    this.label_layer.graphics.removeAll();
    this.mouse_label_layer.graphics.removeAll();
  };

  // Remove the Click Handler if set
  clearClickHandler() {
    if (this.click_handler && this.click_handler !== null) {
      this.click_handler.remove();
      this.click_handler = null;
    }
  }

  calculateParcelDimension(geometry) {
    // this.cancelTooltip();
    this.setTooltipMessage(null);

    // console.log("parcel", geometry);
    var queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];
    queryObj.geometry = geometry;

    query
      .executeQueryJSON(this.parcels_service_url, queryObj)
      .then((results) => {
        this.clearGraphics();

        const graphic = new Graphic({
          geometry: results.features[0].geometry,
          symbol: {
            type: "simple-fill",
            outline: {
              width: 3,
              color: [255, 0, 0, 0.8],
              cap: "round",
              join: "round",
            },
            color: [26, 26, 26, 0.17],
          },
        });

        // Set the area unit to acre
        this.areaUnit = "acres";

        // Pass the geometry and units to the Measurement widget
        this.setGeometry(results.features[0].geometry);
        this.setAreaUnit(this.areaUnit);
        this.setLengthUnit(this.lengthUnit);

        // clear the active state now that the geometry has been calculated
        this.setActive(false);

        // console.log(results.features[0].geometry.rings[0]);

        // calculate the midpoint labels
        const calculatePomises = [];
        const renderPromises = [];

        var point = null;
        results.features[0].geometry.rings[0].forEach((geoemtry) => {
          var modified = {
            x: geoemtry[0],
            y: geoemtry[1],
          };
          if (point === null) {
            point = modified;
          } else {
            this.calculateSimpleDistance(point, modified, (result) => {
              // console.log("calculateSimpleDistance Result", result);
              calculatePomises.push(result);
              point = modified;
            });
          }
        });

        Promise.all(calculatePomises)
          .then((arrOfResults) => {
            // console.log("promises", arrOfResults);
            arrOfResults.forEach((result) => {
              this.renderMidPoint(result, (graphic) => {
                renderPromises.push(graphic);
              });
            });
          })
          .then((result) => {
            Promise.all(renderPromises).then((arrOfResults) => {
              // console.log("renderPromises Result", { arrOfResults });

              this.mouse_label_layer.graphics.removeAll();
              this.mouse_label_layer.graphics.addMany(arrOfResults);
            });
          });

        // label graphic
        this.layer.add(graphic);
      });
  }
}
